const createStyles = (styles: React.CSSProperties): React.CSSProperties => styles;

export const verificationModalOverlayStyles = createStyles({
	position: 'fixed',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	// `100dvh` keeps the overlay sized to the visible viewport on iOS Safari
	// when the soft keyboard opens, instead of overflowing under the keyboard.
	height: '100dvh',
	maxHeight: '100dvh',
	backgroundColor: 'var(--color-bg-overlay)',
	backdropFilter: 'blur(8px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 1050,
	animation: 'modalFadeIn 0.2s ease-out',
	padding: '16px',
	paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
	overflowY: 'auto',
});

export const verificationModalContainerStyles = createStyles({
	backgroundColor: 'var(--color-bg-elevated)',
	borderRadius: 'var(--radius-xl)',
	boxShadow: 'var(--shadow-xl)',
	width: '100%',
	maxWidth: '420px',
	maxHeight: 'calc(100dvh - 32px)',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	animation: 'modalScaleIn 0.2s ease-out',
});

// Sticky footer wrapper for the primary action button so it sits above the
// iOS keyboard on small viewports.
export const verificationModalFooterStyles = createStyles({
	position: 'sticky',
	bottom: 0,
	backgroundColor: 'var(--color-bg-elevated)',
	paddingTop: '12px',
	zIndex: 1,
});

export const modalHeaderStyles = createStyles({
	background: 'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-secondary) 100%)',
	padding: '32px 24px',
	textAlign: 'center',
	color: '#ffffff',
});

export const iconContainerStyles = createStyles({
	width: '64px',
	height: '64px',
	margin: '0 auto 16px',
	backgroundColor: 'rgba(255, 255, 255, 0.2)',
	borderRadius: '50%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	fontSize: '28px',
	animation: 'phoneVerifyPulse 2s infinite',
});

export const modalTitleStyles = createStyles({
	margin: '0 0 8px',
	fontSize: 'var(--type-md)',
	fontFamily: 'var(--font-family-semibold)',
	color: '#ffffff',
});

export const modalSubtitleStyles = createStyles({
	margin: 0,
	fontSize: 'var(--type-sm)',
	fontFamily: 'var(--font-family-regular)',
	color: 'rgba(255, 255, 255, 0.9)',
	lineHeight: 'var(--leading-normal)',
});

export const modalBodyStyles = createStyles({
	padding: '24px',
	overflowY: 'auto',
	flex: 1,
	minHeight: 0,
});

export const formGroupStyles = createStyles({
	marginBottom: '20px',
});

export const formLabelStyles = createStyles({
	display: 'block',
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--text-label-size)',
	color: 'var(--color-text-primary)',
	marginBottom: '8px',
});

export const getPhoneInputWrapperStyles = (hasError: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'stretch',
	width: '100%',
	borderRadius: 'var(--radius-default)',
	border: `1px solid ${hasError ? 'var(--color-error)' : 'var(--color-border-default)'}`,
	backgroundColor: 'var(--color-bg-primary)',
	transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
	overflow: 'hidden',
});

export const phonePrefixStyles = createStyles({
	display: 'flex',
	alignItems: 'center',
	padding: '0 12px',
	backgroundColor: 'var(--color-bg-secondary)',
	borderRight: '1px solid var(--color-border-default)',
	color: 'var(--color-text-secondary)',
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--text-body-size)',
	flexShrink: 0,
	userSelect: 'none',
});

export const phoneInputStyles = createStyles({
	flex: 1,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-size)',
	outline: 'none',
	border: 'none',
	height: '44px',
	paddingLeft: '12px',
	paddingRight: '12px',
	backgroundColor: 'transparent',
	color: 'var(--color-text-primary)',
	minWidth: 0,
});

export const deliveryMethodContainerStyles = createStyles({
	display: 'flex',
	gap: '8px',
	padding: '4px',
	backgroundColor: 'var(--color-bg-secondary)',
	borderRadius: 'var(--radius-full)',
});

export const getDeliveryMethodOptionStyles = (isActive: boolean): React.CSSProperties => ({
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	padding: '10px 16px',
	border: 'none',
	borderRadius: 'var(--radius-full)',
	cursor: 'pointer',
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--type-sm)',
	transition: 'all var(--transition-fast)',
	backgroundColor: isActive ? 'var(--color-bg-elevated)' : 'transparent',
	color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
	boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
});

export const getSubmitButtonStyles = (disabled: boolean): React.CSSProperties => ({
	width: '100%',
	height: '48px',
	backgroundColor: disabled ? 'var(--color-bg-tertiary)' : 'var(--color-brand-secondary)',
	color: disabled ? 'var(--color-text-disabled)' : '#ffffff',
	border: 'none',
	borderRadius: 'var(--radius-default)',
	fontFamily: 'var(--font-family-semibold)',
	fontSize: 'var(--text-body-size)',
	cursor: disabled ? 'not-allowed' : 'pointer',
	transition: 'all var(--transition-fast)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
});

export const errorMessageStyles = createStyles({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	padding: '12px',
	backgroundColor: 'var(--color-error-bg)',
	borderRadius: 'var(--radius-default)',
	marginBottom: '16px',
	color: 'var(--color-error-text)',
	fontSize: 'var(--type-sm)',
	fontFamily: 'var(--font-family-regular)',
	animation: 'phoneVerifyShake 0.5s ease',
});

export const otpContainerStyles = createStyles({
	display: 'flex',
	justifyContent: 'center',
	gap: '8px',
	marginBottom: '20px',
});

export const getOtpDigitBoxStyles = (hasError: boolean): React.CSSProperties => ({
	width: '48px',
	height: '56px',
	border: `2px solid ${hasError ? 'var(--color-error)' : 'var(--color-border-default)'}`,
	borderRadius: 'var(--radius-md)',
	backgroundColor: 'var(--color-bg-primary)',
	color: 'var(--color-text-primary)',
	fontSize: 'var(--type-md)',
	fontFamily: 'var(--font-family-semibold)',
	textAlign: 'center',
	outline: 'none',
	transition: 'all var(--transition-fast)',
});

export const getCountdownContainerStyles = (isUrgent: boolean): React.CSSProperties => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '8px',
	padding: '12px 16px',
	backgroundColor: isUrgent ? 'var(--color-error-bg)' : 'var(--color-bg-secondary)',
	borderRadius: 'var(--radius-default)',
	marginBottom: '20px',
	transition: 'background-color var(--transition-fast)',
});

export const getCountdownTextStyles = (isUrgent: boolean): React.CSSProperties => ({
	fontSize: 'var(--type-sm)',
	fontFamily: 'var(--font-family-medium)',
	color: isUrgent ? 'var(--color-error)' : 'var(--color-text-secondary)',
	animation: isUrgent ? 'phoneVerifyPulse 1s infinite' : 'none',
});

export const resendContainerStyles = createStyles({
	textAlign: 'center',
	paddingTop: '16px',
	borderTop: '1px solid var(--color-border-subtle)',
});

export const resendTextStyles = createStyles({
	margin: '0 0 8px',
	fontSize: 'var(--type-sm)',
	fontFamily: 'var(--font-family-regular)',
	color: 'var(--color-text-secondary)',
});

export const resendActionsStyles = createStyles({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '16px',
});

export const getLinkButtonStyles = (disabled: boolean): React.CSSProperties => ({
	background: 'none',
	border: 'none',
	padding: 0,
	fontFamily: 'var(--font-family-medium)',
	fontSize: 'var(--type-sm)',
	color: disabled ? 'var(--color-text-disabled)' : 'var(--color-brand-primary)',
	cursor: disabled ? 'not-allowed' : 'pointer',
	textDecoration: 'underline',
	textUnderlineOffset: '2px',
	transition: 'color var(--transition-fast)',
});

export const dividerStyles = createStyles({
	color: 'var(--color-border-default)',
});

export const maskedPhoneTextStyles = createStyles({
	fontFamily: 'var(--font-family-medium)',
	color: 'rgba(255, 255, 255, 0.95)',
});

export const loadingSpinnerStyles = createStyles({
	display: 'inline-block',
	width: '18px',
	height: '18px',
	border: '2px solid transparent',
	borderTopColor: 'currentColor',
	borderRadius: '50%',
	animation: 'phoneVerifySpin 0.8s linear infinite',
});

export const stepTransitionLeftStyles = createStyles({
	animation: 'phoneVerifySlideLeft 0.3s ease-out',
});

export const stepTransitionRightStyles = createStyles({
	animation: 'phoneVerifySlideRight 0.3s ease-out',
});
